(function () {
  'use strict';

  if (customElements.get('giveaway-experience')) return;

  class GiveawayExperience extends HTMLElement {
    connectedCallback() {
      this.listId = this.dataset.klaviyoListId || '';
      this.successUrl = this.dataset.successUrl || '/pages/giveaway-thank-you';
      this.modal = this.querySelector('[data-giveaway-modal]');
      this.dialog = this.querySelector('[data-giveaway-dialog]');
      this.closeButtons = this.querySelectorAll('[data-giveaway-close]');
      this.trigger = this.querySelector('[data-giveaway-trigger]');
      this.externalTriggerSelector = this.dataset.externalTrigger || '';
      this.lastFocusedElement = null;
      this.signupForm = this.querySelector('[data-giveaway-signup-form]');
      this.formStatus = this.querySelector('[data-giveaway-form-status]');

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleExternalClick = this.handleExternalClick.bind(this);
      this.open = this.open.bind(this);
      this.close = this.close.bind(this);

      this.signupForm?.addEventListener('submit', this.handleSubmit);

      if (this.dataset.displayMode === 'popup' && this.modal) {
        this.trigger?.addEventListener('click', this.open);
        this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
        document.addEventListener('click', this.handleExternalClick);
        this.modal.addEventListener('keydown', this.handleKeydown);

        const delay = Number(this.dataset.autoOpenDelay || 0);
        if (delay > 0 && !sessionStorage.getItem('citrusPearGiveawayEntered')) {
          this.openTimer = window.setTimeout(this.open, delay * 1000);
        }
      }
    }

    disconnectedCallback() {
      this.signupForm?.removeEventListener('submit', this.handleSubmit);
      document.removeEventListener('click', this.handleExternalClick);
      this.modal?.removeEventListener('keydown', this.handleKeydown);
      this.trigger?.removeEventListener('click', this.open);
      this.closeButtons?.forEach((button) => button.removeEventListener('click', this.close));
      if (this.openTimer) window.clearTimeout(this.openTimer);
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (!this.signupForm?.reportValidity()) return;

      const submitButton = this.signupForm.querySelector('[type="submit"]');
      const email = String(new FormData(this.signupForm).get('email') || '').trim();

      if (!email || !this.listId) {
        this.showFormError('Something went wrong. Please try again.');
        return;
      }

      this.signupForm.setAttribute('aria-busy', 'true');
      if (submitButton) submitButton.disabled = true;
      if (this.formStatus) this.formStatus.hidden = true;

      const payload = new URLSearchParams({
        g: this.listId,
        email,
        '$fields': '$source,$consent',
        '$source': 'Citrus Pear Giveaway',
        '$consent': 'email'
      });

      try {
        const response = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: payload.toString()
        });
        const result = await response.json();

        if (!response.ok || result.success !== true) throw new Error(`Klaviyo request failed: ${response.status}`);

        sessionStorage.setItem('citrusPearGiveawayEntered', 'true');
        window.location.assign(this.successUrl);
      } catch (error) {
        console.error('[Citrus Pear] Giveaway signup failed.', error);
        this.signupForm.removeAttribute('aria-busy');
        if (submitButton) submitButton.disabled = false;
        this.showFormError('Something went wrong. Please try again.');
      }
    }

    showFormError(message) {
      if (!this.formStatus) return;
      this.formStatus.textContent = message;
      this.formStatus.hidden = false;
    }

    handleExternalClick(event) {
      if (!this.externalTriggerSelector) return;
      const trigger = event.target.closest(this.externalTriggerSelector);
      if (!trigger) return;
      event.preventDefault();
      this.open();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.dialog) return;
      const focusable = Array.from(
        this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.offsetParent !== null);

      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open() {
      if (!this.modal || !this.modal.hidden) return;
      this.lastFocusedElement = document.activeElement;
      this.modal.hidden = false;
      document.documentElement.classList.add('giveaway-popup-open');
      window.requestAnimationFrame(() => this.modal.classList.add('is-open'));
      this.querySelector('[data-giveaway-close]')?.focus();
    }

    close() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.classList.remove('is-open');
      document.documentElement.classList.remove('giveaway-popup-open');
      window.setTimeout(() => {
        this.modal.hidden = true;
        this.lastFocusedElement?.focus?.();
      }, 180);
    }
  }

  customElements.define('giveaway-experience', GiveawayExperience);
})();
